// SCR-STAFF-876 تب سفارش‌ها (US-INV-051، 052): سفارش از درخواست تأییدشده با قیمت هر قلم (پیش‌فرض قرارداد، آخرین خرید یا برآورد درخواست)، صدور و
// PDF سفارش، لغو پیش از نخستین رسید و رسید از سفارش با پیش‌پر مانده هر قلم، بچ و انقضای شمسی و بهای واحد (تحویل جزئی ← مانده باز؛ بیش از مانده
// ← PHX-INV-0315؛ اختلاف قیمت بیش از آستانه ← هشدار).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type InvPurchaseOrderStatus, type InvPurchaseOrderView, type InvPurchaseRequestView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, JalaliDate, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { saveBlob } from '../medorders/common';
import { parseJalaliInput } from '../patients/common';
import { MoneyCell, ORDER_STATUSES, orderTone, procurementKeys, ReasonDialog, rialsOf, SupplierSelect } from './procurementCommon';
import { milliIn, stockKeys, toMilli, useQuantityText } from './stockCommon';

export function PurchaseOrdersTab() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const quantity = useQuantityText();
  const idempotency = useIdempotencyKey();
  const full = access.accessMode === 'Full';
  const manage = full && access.can('inv.po.manage');
  const [status, setStatus] = useState<InvPurchaseOrderStatus | ''>('');
  const [receiving, setReceiving] = useState<InvPurchaseOrderView | null>(null);
  const [cancelling, setCancelling] = useState<InvPurchaseOrderView | null>(null);
  const [notice, setNotice] = useState<string[] | null>(null);

  const orders = useQuery({
    queryKey: [...procurementKeys.orders(session?.tenantId), status],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/purchase-orders', { params: { query: { status: status || undefined, limit: 50 } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: procurementKeys.orders(session?.tenantId) });
  const issue = useMutation({
    mutationFn: (order: InvPurchaseOrderView) =>
      unwrap(tenantApi.POST('/api/v1/inventory/purchase-orders/{id}:issue', { params: { path: { id: order.id } }, headers: { 'Idempotency-Key': idempotency.current() } })),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: refresh,
  });
  const print = useMutation({
    mutationFn: async (order: InvPurchaseOrderView) => {
      const response = await tenantApi.GET('/api/v1/inventory/purchase-orders/{id}/print', { params: { path: { id: order.id }, query: { output: 'Pdf' } }, parseAs: 'blob' });
      if (response.response.ok && response.data) {
        saveBlob(response.data as unknown as Blob, `${order.number}.pdf`);
      }
    },
  });

  const columns: DataTableColumn<InvPurchaseOrderView>[] = [
    { id: 'number', headerKey: 'inv:purchasing.columns.number', cell: (row) => <bdi dir="ltr" data-testid={`order-${row.number}`}>{row.number}</bdi> },
    {
      id: 'status',
      headerKey: 'inv:purchasing.columns.status',
      cell: (row) => (
        <span data-testid={`order-status-${row.number}`}>
          <StatusChip tone={orderTone(row.status)} labelKey={`inv:order_status.${row.status}`} />
        </span>
      ),
    },
    { id: 'supplier', headerKey: 'inv:purchasing.columns.supplier', cell: (row) => row.supplierName },
    { id: 'request', headerKey: 'inv:purchasing.columns.request', cell: (row) => <bdi dir="ltr">{row.purchaseRequestNumber ?? '—'}</bdi> },
    {
      id: 'lines',
      headerKey: 'inv:purchasing.columns.received',
      cell: (row) => (
        <span data-testid={`order-lines-${row.number}`}>
          {row.lines
            .map((line) => `${line.itemName}: ${quantity(line.receivedMilli, line.baseUnit)} / ${quantity(line.quantityMilli, line.baseUnit)}`)
            .join(t('common:list_separator'))}
        </span>
      ),
    },
    { id: 'total', headerKey: 'inv:purchasing.columns.total', cell: (row) => <MoneyCell value={row.total} /> },
    { id: 'delivery', headerKey: 'inv:purchasing.columns.expected_delivery', cell: (row) => (row.expectedDeliveryDate ? <JalaliDate value={row.expectedDeliveryDate} /> : '—') },
    {
      id: 'actions',
      headerKey: 'inv:purchasing.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {row.warnings.map((warning) => (
            <Typography key={warning} variant="caption" color="warning.main">
              {t(`inv:purchasing.warnings.${warning}`)}
            </Typography>
          ))}
          {row.status === 'Approved' && (
            <Button size="small" disabled={!manage || issue.isPending} onClick={() => issue.mutate(row)} data-testid={`order-issue-${row.number}`}>
              {t('inv:purchasing.issue')}
            </Button>
          )}
          {(row.status === 'Issued' || row.status === 'PartiallyReceived') && (
            <Button size="small" variant="outlined" disabled={!full || !access.can('inv.receipt.manage')} onClick={() => setReceiving(row)} data-testid={`order-receive-${row.number}`}>
              {t('inv:purchasing.receive')}
            </Button>
          )}
          {row.status !== 'Cancelled' && (
            <Button size="small" disabled={!access.can('inv.po.manage') || print.isPending} onClick={() => print.mutate(row)} data-testid={`order-print-${row.number}`}>
              {t('inv:purchasing.print')}
            </Button>
          )}
          {(row.status === 'Approved' || row.status === 'Issued') && (
            <Button size="small" color="inherit" disabled={!manage} onClick={() => setCancelling(row)} data-testid={`order-cancel-${row.number}`}>
              {t('inv:purchasing.cancel')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <TextField select size="small" label={t('inv:purchasing.columns.status')} value={status} onChange={(event) => setStatus(event.target.value as InvPurchaseOrderStatus | '')} sx={{ maxWidth: 240 }}>
        <MenuItem value="">{t('inv:items.filters.all')}</MenuItem>
        {ORDER_STATUSES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`inv:order_status.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      {issue.error && <Alert severity="error">{t('inv:purchasing.action_failed')}</Alert>}
      {notice && (
        <Alert severity={notice.length ? 'warning' : 'success'} onClose={() => setNotice(null)} data-testid="order-receipt-notice">
          {notice.length ? notice.map((warning) => t(`inv:documents.warnings.${warning}`)).join(t('common:list_separator')) : t('inv:purchasing.received_ok')}
        </Alert>
      )}
      <DataTable
        captionKey="inv:purchasing.orders_caption"
        columns={columns}
        rows={orders.data?.items}
        getRowId={(row) => row.id}
        isLoading={orders.isPending}
        error={orders.error}
        onRetry={() => void orders.refetch()}
        emptyTitleKey="inv:purchasing.orders_empty"
      />
      {receiving && (
        <ReceiveOrderDialog
          order={receiving}
          onClose={() => setReceiving(null)}
          onSaved={(warnings) => {
            setReceiving(null);
            setNotice(warnings);
            refresh();
            void queryClient.invalidateQueries({ queryKey: stockKeys.documents(session?.tenantId) });
          }}
        />
      )}
      {cancelling && (
        <ReasonDialog
          titleKey="inv:purchasing.cancel_order_title"
          screenId="SCR-STAFF-876-ORDER-CANCEL"
          onClose={() => setCancelling(null)}
          onConfirm={async (reason, key) => {
            await unwrap(tenantApi.POST('/api/v1/inventory/purchase-orders/{id}:cancel', { params: { path: { id: cancelling.id } }, body: { reason }, headers: { 'Idempotency-Key': key } }));
            setCancelling(null);
            refresh();
          }}
        />
      )}
    </Stack>
  );
}

/** سفارش از درخواست تأییدشده؛ قیمت هر قلم قابل اصلاح (افزایش بیش از مبلغ تأییدشده و سقف ← PHX-INV-0314). */
export function OrderCreateDialog({ request, onClose, onSaved }: { request: InvPurchaseRequestView; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [supplierId, setSupplierId] = useState(request.supplierId ?? '');
  const [delivery, setDelivery] = useState('');
  const [note, setNote] = useState('');
  const [prices, setPrices] = useState<Record<number, string>>(Object.fromEntries(request.lines.map((line) => [Number(line.lineNo), String(line.unitPrice.amount)])));
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/purchase-orders', {
          body: {
            purchaseRequestId: request.id,
            supplierId: supplierId || null,
            expectedDeliveryDate: parseJalaliInput(delivery) ?? null,
            note: note.trim() || null,
            prices: request.lines
              .filter((line) => rialsOf(prices[Number(line.lineNo)] ?? '') !== null && rialsOf(prices[Number(line.lineNo)] ?? '') !== Number(line.unitPrice.amount))
              .map((line) => ({ lineNo: Number(line.lineNo), unitPrice: { amount: rialsOf(prices[Number(line.lineNo)] ?? '') ?? 0, currency: 'IRR' } })),
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onSaved,
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-876-ORDER-FORM"
      titleKey="inv:purchasing.order_title"
      onSubmit={() => (supplierId ? save.mutate() : undefined)}
      onClose={onClose}
      isDirty={false}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      <Typography>
        {t('inv:purchasing.from_request', { number: request.number })} · <MoneyCell value={request.estimatedTotal} />
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SupplierSelect value={supplierId} onChange={setSupplierId} testId="order-supplier" required />
        <TextField
          size="small"
          label={t('inv:purchasing.expected_delivery')}
          placeholder="1405/08/01"
          value={delivery}
          onChange={(event) => setDelivery(event.target.value)}
          error={Boolean(delivery.trim()) && !parseJalaliInput(delivery)}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'order-delivery' } }}
        />
      </Stack>
      {request.lines.map((line) => (
        <Stack key={line.lineNo} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography sx={{ minWidth: 220 }}>
            {line.itemName} · {t(`inv:unit.${line.unit}`)} · {t(`inv:price_source.${line.priceSource}`)}
          </Typography>
          <TextField
            size="small"
            label={t('inv:purchasing.unit_price')}
            value={prices[Number(line.lineNo)] ?? ''}
            onChange={(event) => setPrices((current) => ({ ...current, [Number(line.lineNo)]: event.target.value }))}
            error={rialsOf(prices[Number(line.lineNo)] ?? '') === null}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': `order-price-${line.lineNo}` } }}
          />
        </Stack>
      ))}
      <Alert severity="info">{t('inv:purchasing.order_price_note')}</Alert>
      <TextField size="small" label={t('inv:documents.note')} value={note} onChange={(event) => setNote(event.target.value)} />
    </FormDialog>
  );
}

interface ReceiveLineState {
  quantity: string;
  batchNumber: string;
  expiryDate: string;
  unitCost: string;
}

function ReceiveOrderDialog({ order, onClose, onSaved }: { order: InvPurchaseOrderView; onClose: () => void; onSaved: (warnings: string[]) => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const quantity = useQuantityText();
  const open = order.lines.filter((line) => Number(line.remainingMilli) > 0);
  const [lines, setLines] = useState<Record<number, ReceiveLineState>>(
    Object.fromEntries(
      open.map((line) => [
        Number(line.lineNo),
        { quantity: String(Number(milliIn(line.remainingMilli, line.unit, line.baseUnit, line.conversions).toFixed(3))), batchNumber: '', expiryDate: '', unitCost: '' },
      ]),
    ),
  );
  const update = (lineNo: number, patch: Partial<ReceiveLineState>) => setLines((current) => ({ ...current, [lineNo]: { ...current[lineNo]!, ...patch } }));
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/purchase-orders/{id}:receive', {
          params: { path: { id: order.id } },
          body: {
            invoiceFileId: null,
            note: null,
            lines: open
              .map((line) => ({ line, state: lines[Number(line.lineNo)]! }))
              .filter(({ state }) => (toMilli(state.quantity) ?? 0) > 0)
              .map(({ line, state }) => {
                const cost = rialsOf(state.unitCost);
                return {
                  orderLineNo: Number(line.lineNo),
                  quantityMilli: toMilli(state.quantity) ?? 0,
                  unit: line.unit,
                  batchNumber: state.batchNumber.trim() || null,
                  expiryDate: parseJalaliInput(state.expiryDate) ?? null,
                  ...(cost === null ? {} : { unitCost: { amount: cost, currency: 'IRR' } }),
                };
              }),
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (document) => onSaved(document.warnings),
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-876-RECEIVE"
      titleKey="inv:purchasing.receive_title"
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={false}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="lg"
    >
      <Typography>
        {order.number} · {order.supplierName}
      </Typography>
      {open.map((line) => {
        const state = lines[Number(line.lineNo)]!;
        return (
          <Stack key={line.lineNo} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <Typography sx={{ minWidth: 240 }}>
              {line.itemName} · {t('inv:purchasing.remaining')}: {quantity(line.remainingMilli, line.baseUnit)}
            </Typography>
            <TextField
              size="small"
              label={`${t('inv:documents.received')} (${t(`inv:unit.${line.unit}`)})`}
              value={state.quantity}
              onChange={(event) => update(Number(line.lineNo), { quantity: event.target.value })}
              sx={{ width: 160 }}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': `receive-order-quantity-${line.lineNo}` } }}
            />
            {line.tracksBatch && (
              <>
                <TextField size="small" required label={t('inv:documents.batch_number')} value={state.batchNumber} onChange={(event) => update(Number(line.lineNo), { batchNumber: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': `receive-order-batch-${line.lineNo}` } }} />
                <TextField
                  size="small"
                  required={line.tracksExpiry}
                  label={t('inv:documents.expiry')}
                  placeholder="1406/03/10"
                  value={state.expiryDate}
                  onChange={(event) => update(Number(line.lineNo), { expiryDate: event.target.value })}
                  error={Boolean(state.expiryDate.trim()) && !parseJalaliInput(state.expiryDate)}
                  slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': `receive-order-expiry-${line.lineNo}` } }}
                />
              </>
            )}
            <TextField
              size="small"
              label={t('inv:documents.unit_cost')}
              placeholder={String(line.unitPrice.amount)}
              helperText={t('inv:purchasing.unit_cost_help')}
              value={state.unitCost}
              onChange={(event) => update(Number(line.lineNo), { unitCost: event.target.value })}
              sx={{ width: 200 }}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': `receive-order-cost-${line.lineNo}` } }}
            />
          </Stack>
        );
      })}
    </FormDialog>
  );
}
